import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import type { SentenceClause, SentenceReorderClausesContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Restore each clause's word order (mirrors iOS SentenceReorderClausesView:
 *  per-clause token order must equal correct_order). Answer is a per-clause
 *  map of ordered token ids. */
export function SentenceReorderClausesRenderer({ content, answer, setAnswer, revealed }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as SentenceReorderClausesContent;
  const clauses = c.clauses ?? [];
  const map = (answer as Record<string, string[]>) ?? {};

  const tokenText = (cl: SentenceClause, id: string) =>
    cl.tokens.find((tk) => tk.token_id === id)?.text ?? id;

  const place = (i: number, id: string) => {
    const cur = map[String(i)] ?? [];
    if (cur.includes(id)) return;
    setAnswer({ ...map, [String(i)]: [...cur, id] });
  };
  const unplace = (i: number, id: string) => {
    const cur = map[String(i)] ?? [];
    setAnswer({ ...map, [String(i)]: cur.filter((x) => x !== id) });
  };

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      <div className="mt-4 space-y-4">
        {clauses.map((cl, i) => {
          const placed = map[String(i)] ?? [];
          const pool = cl.tokens.filter((tk) => !placed.includes(tk.token_id));
          const ok = revealed
            ? placed.length === cl.correct_order.length && placed.every((id, j) => id === cl.correct_order[j])
            : null;
          return (
            <div
              key={i}
              className={`rounded-xl border p-3 ${
                revealed ? (ok ? "border-emerald-400 bg-emerald-50" : "border-red-400 bg-red-50") : "border-black/10 bg-slate-50"
              }`}
            >
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                {cl.role_label_de || cl.role}
              </div>
              {/* Built order */}
              <div className="mb-2 flex min-h-[2.25rem] flex-wrap items-center gap-1.5 rounded-lg bg-white/70 p-1.5">
                {placed.length === 0 && <span className="px-1 text-xs text-ink-faint">{t("Tap words in order…")}</span>}
                {placed.map((id) => (
                  <button
                    key={id}
                    type="button"
                    disabled={revealed}
                    onClick={() => unplace(i, id)}
                    className="rounded-md bg-brand-100 px-2.5 py-1 text-sm text-brand-800 disabled:cursor-default"
                  >
                    {tokenText(cl, id)}
                  </button>
                ))}
              </div>
              {/* Pool */}
              {!revealed && pool.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {pool.map((tk) => (
                    <button
                      key={tk.token_id}
                      type="button"
                      onClick={() => place(i, tk.token_id)}
                      className={cx("rounded-md border border-black/10 bg-white px-2.5 py-1 text-sm hover:border-brand-300")}
                    >
                      {tk.text}
                    </button>
                  ))}
                </div>
              )}
              {revealed && !ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {t("Correct:")}{" "}
                  <span className="font-semibold">{cl.correct_order.map((id) => tokenText(cl, id)).join(" ")}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
